// src/components/Pentests/runs/ShodanView.js
//
// Shodan results: one ScanResult row per (host, port). We aggregate by IP
// so the view shows one card per host with its ports, services, and
// vulnerabilities collected across rows.

import { useMemo } from 'react';
import { ExternalLink, Globe, ShieldAlert } from 'lucide-react';

const SEVERITY_STYLE = {
  critical: 'bg-red-100 text-red-700 border-red-300',
  high: 'bg-orange-100 text-orange-700 border-orange-300',
  medium: 'bg-yellow-100 text-yellow-700 border-yellow-300',
  low: 'bg-blue-100 text-blue-700 border-blue-300',
  info: 'bg-slate-100 text-slate-700 border-slate-300',
};

function severityFromCvss(score) {
  if (score == null) return 'info';
  const n = Number(score);
  if (n >= 9) return 'critical';
  if (n >= 7) return 'high';
  if (n >= 4) return 'medium';
  if (n > 0) return 'low';
  return 'info';
}

function aggregateByHost(results) {
  const map = new Map();
  for (const r of results) {
    const d = r.data || {};
    const ip = d.ip || r.value;
    if (!ip) continue;
    let entry = map.get(ip);
    if (!entry) {
      entry = {
        ip,
        org: d.org,
        isp: d.isp,
        asn: d.asn,
        city: d.city,
        country: d.country || d.country_name,
        domain: d.domain,
        hostnames: new Set(),
        ports: new Map(),
        vulns: new Map(),
      };
      map.set(ip, entry);
    }
    if (d.org && !entry.org) entry.org = d.org;
    if (d.isp && !entry.isp) entry.isp = d.isp;
    if (d.asn && !entry.asn) entry.asn = d.asn;
    if (d.city && !entry.city) entry.city = d.city;
    if (d.country && !entry.country) entry.country = d.country;
    if (d.domain && !entry.domain) entry.domain = d.domain;
    if (Array.isArray(d.hostnames)) d.hostnames.forEach((h) => entry.hostnames.add(h));

    if (d.port != null) {
      entry.ports.set(d.port, {
        port: d.port,
        service: d.service || '',
        version: d.version || '',
        protocol: d.protocol || 'tcp',
        banner: d.banner || '',
      });
    }

    const vd = d.vulns_details || {};
    const isList = Array.isArray(d.vulns);
    if (isList) {
      d.vulns.forEach((cve) => {
        if (!entry.vulns.has(cve)) entry.vulns.set(cve, { id: cve });
      });
    }
    Object.entries(vd).forEach(([cve, v]) => {
      const sev = v?.severity || severityFromCvss(v?.cvss);
      entry.vulns.set(cve, {
        id: cve,
        cvss: v?.cvss ?? null,
        severity: sev,
        summary: v?.summary || '',
      });
    });
  }
  return [...map.values()].map((e) => ({
    ...e,
    hostnames: [...e.hostnames],
    ports: [...e.ports.values()].sort((a, b) => Number(a.port) - Number(b.port)),
    vulns: [...e.vulns.values()],
  }));
}

export default function ShodanView({ results }) {
  const hosts = useMemo(() => aggregateByHost(results), [results]);

  if (hosts.length === 0) {
    return (
      <div className="rounded-2xl border border-cyber-300/40 bg-cyber-50/60 p-10 text-center text-slate-500">
        <Globe className="w-6 h-6 mx-auto mb-2 opacity-60" />
        No Shodan data yet.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {hosts.map((h) => (
        <div key={h.ip} className="rounded-2xl border border-cyber-300/40 bg-cyber-50/60 p-4 space-y-3">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Host</div>
              <div className="font-mono text-shinobi-green text-base break-all">{h.ip}</div>
              {h.hostnames.length > 0 && (
                <div className="text-xs text-slate-500 mt-1 break-all">{h.hostnames.join(', ')}</div>
              )}
              {h.domain && !h.hostnames.includes(h.domain) && (
                <div className="text-xs text-slate-500 mt-0.5 break-all">domain: {h.domain}</div>
              )}
            </div>
            <div className="text-right text-xs text-slate-500 space-y-0.5">
              {h.org && <div>org: <span className="text-slate-800">{h.org}</span></div>}
              {h.isp && h.isp !== h.org && <div>isp: <span className="text-slate-800">{h.isp}</span></div>}
              {h.asn && <div>asn: <span className="text-slate-800">{h.asn}</span></div>}
              {(h.city || h.country) && (
                <div>geo: <span className="text-slate-800">{[h.city, h.country].filter(Boolean).join(', ')}</span></div>
              )}
            </div>
          </div>

          {h.ports.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
                Open ports ({h.ports.length})
              </div>
              <div className="overflow-hidden rounded-lg border border-cyber-300/40">
                <table className="w-full text-xs text-left">
                  <thead className="bg-cyber-200/60 text-[10px] uppercase tracking-wider text-slate-700">
                    <tr>
                      <th className="px-3 py-2 text-right">Port</th>
                      <th className="px-3 py-2">Proto</th>
                      <th className="px-3 py-2">Service</th>
                      <th className="px-3 py-2">Banner</th>
                    </tr>
                  </thead>
                  <tbody>
                    {h.ports.map((p) => (
                      <tr key={p.port} className="border-t border-cyber-300/30">
                        <td className="px-3 py-1.5 text-right font-mono text-shinobi-green">{p.port}</td>
                        <td className="px-3 py-1.5 text-slate-500 uppercase">{p.protocol}</td>
                        <td className="px-3 py-1.5 font-mono text-slate-800">
                          {[p.service, p.version].filter(Boolean).join(' ') || '-'}
                        </td>
                        <td className="px-3 py-1.5 font-mono text-[11px] text-slate-500 max-w-md truncate">
                          {p.banner ? p.banner.replace(/\r?\n/g, ' ').slice(0, 200) : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {h.vulns.length > 0 && (
            <div>
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-slate-500 mb-1">
                <ShieldAlert className="w-3 h-3" />
                Vulnerabilities ({h.vulns.length})
              </div>
              <div className="overflow-hidden rounded-lg border border-cyber-300/40">
                <table className="w-full text-xs text-left">
                  <thead className="bg-cyber-200/60 text-[10px] uppercase tracking-wider text-slate-700">
                    <tr>
                      <th className="px-3 py-2">CVE</th>
                      <th className="px-3 py-2">CVSS</th>
                      <th className="px-3 py-2">Severity</th>
                      <th className="px-3 py-2">Summary</th>
                    </tr>
                  </thead>
                  <tbody>
                    {h.vulns.slice(0, 50).map((v) => {
                      const sev = v.severity || severityFromCvss(v.cvss);
                      return (
                        <tr key={v.id} className="border-t border-cyber-300/30">
                          <td className="px-3 py-1.5">
                            <a
                              href={`https://nvd.nist.gov/vuln/detail/${v.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-mono text-shinobi-green hover:underline inline-flex items-center gap-1"
                            >
                              {v.id}
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          </td>
                          <td className="px-3 py-1.5 font-mono text-slate-800">{v.cvss ?? '-'}</td>
                          <td className="px-3 py-1.5">
                            <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] uppercase border ${SEVERITY_STYLE[sev]}`}>
                              {sev}
                            </span>
                          </td>
                          <td className="px-3 py-1.5 text-slate-500">
                            {(v.summary || '').slice(0, 240)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {h.vulns.length > 50 && (
                  <div className="px-3 py-2 text-[10px] text-slate-500 bg-cyber-200/30">
                    Showing first 50 of {h.vulns.length}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
